import {
  Configuration,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * MSAL configuration for the Admin SPA per the 2026-05-29 admin panel
 * design §Security:
 *
 *   - PKCE redirect-mode (not popup — popups hit blockers; admin is
 *     desktop-first and the SWA hostname makes redirect cheap)
 *   - sessionStorage cache (clears on tab close; smaller cross-tab
 *     exposure window than localStorage)
 *   - Scope `api://<adminClientId>/admin_access`
 *
 * Values come from env vars (Vite, selected by --mode): `.env.production` for
 * the deploy build against the production Entra tenant, `.env.offline` for the
 * local dev server against the local OIDC issuer.
 * The hardcoded fallbacks below match prod so the app still boots if a
 * var is missing. `.env.local` (gitignored) is for optional personal
 * overrides only — it no longer selects the environment.
 */

function env(name: string, fallback?: string): string {
  const value = (import.meta.env[name] as string | undefined) ?? fallback;
  if (!value) {
    throw new Error(
      `Missing required env var ${name}. Run \`npm run dev:offline\` for local dev, or build with \`npm run build\` (configs in .env.offline / .env.production).`,
    );
  }
  return value;
}

export const ENTRA_TENANT_ID = env(
  'VITE_ENTRA_TENANT_ID',
  'c4ce1271-d379-4450-9261-4880f94e2986',
);

export const ADMIN_CLIENT_ID = env('VITE_ADMIN_CLIENT_ID');

export const ENTRA_AUTHORITY = env(
  'VITE_ENTRA_AUTHORITY',
  `https://vendoiqusers.ciamlogin.com/${ENTRA_TENANT_ID}`,
);

export const ENTRA_KNOWN_AUTHORITY = env(
  'VITE_ENTRA_KNOWN_AUTHORITY',
  'vendoiqusers.ciamlogin.com',
);

export const API_SCOPE = `api://${ADMIN_CLIENT_ID}/admin_access`;

const msalConfig: Configuration = {
  auth: {
    clientId: ADMIN_CLIENT_ID,
    authority: ENTRA_AUTHORITY,
    knownAuthorities: [ENTRA_KNOWN_AUTHORITY],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message) => {
        if (level <= LogLevel.Warning) {
          // eslint-disable-next-line no-console
          console.warn(`[msal] ${message}`);
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Pull a fresh access token for the admin scope. Silent first; falls
 * back to interactive redirect when refresh fails. Returned tokens are
 * meant for one request — don't cache.
 */
export async function acquireAdminToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // Caller is expected to be inside <AuthenticatedTemplate>. If not,
    // kick to login rather than throwing a cryptic error mid-call.
    await msalInstance.loginRedirect({ scopes: [API_SCOPE] });
    throw new Error('Redirecting to login — no active account');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      account: accounts[0],
      scopes: [API_SCOPE],
    });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE] });
    // acquireTokenRedirect navigates away; the throw is unreachable but
    // typescript needs it to satisfy the return type.
    throw new Error('Redirecting to acquire fresh token');
  }
}
